import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, Bot, Globe, Sparkles, Send, Brain, Loader2, Link2, Clock, Users, Zap, CheckCircle2, Lock, DollarSign, Trash2 } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from "@clerk/clerk-react";
import { toast } from "sonner";
import MarkdownRenderer from "./MarkdownRenderer";
import NetworkGraph from "./NetworkGraph";
import FloatingNav from "./FloatingNav";
import { getAgentHome, searchAgents, getAgent, getRelatedAgents, getAgentHistory, getAgentNetwork, agentChatStream, routerQuery, getMyAgentNetwork, buyAgentAccess, checkAgentAccess, getPaymentStatus, adminCheck, adminDeleteAgent } from "../lib/api";

const PROVIDER_COLORS = { openai: "#10B981", anthropic: "#F5DF4D", groq: "#3B82F6", openrouter: "#A78BFA" };

function AgentCard({ agent, onClick, compact = false, isAdmin = false, onDelete }) {
  const color = PROVIDER_COLORS[agent.provider_type] || "#94A3B8";
  const isFree = (agent.pricing_mode || "free") === "free";
  return (
    <div data-testid={`agent-card-${agent.id}`} onClick={() => onClick(agent)}
      className={`bg-[#111620] border border-[#273142] hover:border-[#E5FF00]/40 transition-all cursor-pointer group ${compact ? "p-3" : "p-5"} rounded-xl relative`}>
      {isAdmin && onDelete && (
        <button data-testid={`delete-agent-${agent.id}`}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(agent); }}
          className="absolute top-3 right-3 text-[#475569] hover:text-[#FF3366] transition-all p-2 rounded-lg hover:bg-[#FF3366]/10 bg-[#1C2433] border border-[#273142] z-10">
          <Trash2 size={14} />
        </button>
      )}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: color + "20", border: `1px solid ${color}40` }}>
          {isFree ? <Bot size={18} style={{ color }} /> : <Lock size={16} className="text-[#E5FF00]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold truncate ${compact ? "text-xs" : "text-sm"}`}>{agent.name}</h3>
            <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full border ${isFree ? "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10" : "text-[#E5FF00] border-[#E5FF00]/30 bg-[#E5FF00]/10"}`}>
              {isFree ? "FREE" : `$${(agent.access_price || 5).toFixed(2)}`}
            </span>
          </div>
          {!compact && <p className="text-xs text-[#94A3B8] line-clamp-2 mt-0.5">{agent.description || "No description"}</p>}
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            {agent.provider_type && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#1C2433] border border-[#273142] text-[#475569]">{agent.provider_type}</span>}
            <span className="font-mono text-[10px] text-[#475569]">{agent.usage_count || 0} uses</span>
            {(agent.edge_count > 0) && <span className="font-mono text-[10px] text-[#475569] flex items-center gap-0.5"><Link2 size={9}/>{agent.edge_count}</span>}
            {agent.tags?.length > 0 && !compact && agent.tags.slice(0, 3).map(t => <span key={t} className="font-mono text-[10px] text-[#E5FF00]/60">#{t}</span>)}
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentChatView({ agent, related, user, onBack, onSelectRelated }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const [deepReasoning, setDeepReasoning] = useState(false);
  const scrollRef = useRef(null);
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) { const el = scrollRef.current.querySelector("[data-radix-scroll-area-viewport]"); if (el) el.scrollTop = el.scrollHeight; }
  }, []);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { if (!agent || !user) return; getAgentHistory(agent.id).then(res => setMessages(res.data)).catch(() => {}); }, [agent, user]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isSending || !user) return;
    const text = input.trim(); setInput(""); setIsSending(true);
    const tempUserId = "u-" + Date.now();
    setMessages(prev => [...prev, { id: tempUserId, role: "user", content: text, created_at: new Date().toISOString() }]);
    const streamId = "s-" + Date.now();
    setMessages(prev => [...prev, { id: streamId, role: "assistant", content: "", _streaming: true, created_at: new Date().toISOString() }]);
    try {
      await agentChatStream(agent.id, text, { web_search: webSearch, deep_reasoning: deepReasoning },
        (chunk) => setMessages(prev => prev.map(m => m.id === streamId ? { ...m, content: m.content + chunk } : m)),
        (meta) => setMessages(prev => prev.map(m => m.id === tempUserId ? { ...m, id: meta.user_msg_id || tempUserId } : m)),
        (data) => { setMessages(prev => prev.map(m => m.id === streamId ? { ...m, id: data.message_id || streamId, content: data.content || m.content, _streaming: false } : m)); setIsSending(false); },
        (detail) => { toast.error(detail); setMessages(prev => prev.filter(m => m.id !== streamId)); setIsSending(false); }
      );
    } catch (e) { toast.error(e.message || "Failed"); setMessages(prev => prev.filter(m => m.id !== tempUserId && m.id !== streamId)); setIsSending(false); }
  };

  const color = PROVIDER_COLORS[agent?.provider_type] || "#94A3B8";
  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col">
        <div className="px-6 py-3 border-b border-[#273142] flex items-center gap-3">
          <button onClick={onBack} data-testid="agent-chat-back" className="text-[#475569] hover:text-white transition-colors"><ArrowLeft size={18}/></button>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "20", border: `1px solid ${color}40` }}><Bot size={16} style={{ color }} /></div>
          <div><h2 className="text-sm font-semibold">{agent.name}</h2><p className="text-[10px] text-[#475569] font-mono uppercase">{agent.provider_type}/{agent.model_name}</p></div>
          <div className="ml-auto flex items-center gap-2"><Brain size={12} className="text-[#E5FF00]"/><span className="font-mono text-[10px] text-[#E5FF00]">Memory</span></div>
        </div>
        <ScrollArea ref={scrollRef} className="flex-1">
          <div className="min-h-full flex flex-col justify-end">
            {messages.length === 0 && <div className="flex-1 flex items-center justify-center py-20"><div className="text-center"><Bot size={32} className="mx-auto mb-3" style={{ color }} /><p className="text-sm text-[#94A3B8]">{agent.description}</p>{!user && <p className="text-xs text-[#E5FF00] mt-2">Sign in to chat</p>}{user && <p className="text-xs text-[#475569] mt-2 font-mono">Start a conversation</p>}</div></div>}
            {messages.map(msg => (
              <div key={msg.id} className={`w-full p-5 flex gap-3 animate-fade-in ${msg.role === "assistant" ? "bg-[#111620] border-l-2 border-l-[#E5FF00]" : ""}`}>
                <div className="flex-shrink-0 w-6 h-6 rounded-md border border-[#273142] flex items-center justify-center">
                  {msg.role === "user" ? <span className="text-[10px] font-mono text-[#94A3B8]">U</span> : <Bot size={12} style={{ color }} />}
                </div>
                <div className="flex-1 min-w-0 text-sm leading-relaxed break-words">
                  {msg.role === "assistant" ? <><MarkdownRenderer content={msg.content} />{msg._streaming && <span className="inline-block w-2 h-4 bg-[#E5FF00] animate-blink ml-0.5 align-middle"/>}</> : <span className="whitespace-pre-wrap">{msg.content}</span>}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        {user ? (
          <div className="border-t border-[#273142]">
            <div className="px-4 py-1.5 flex items-center gap-2 border-b border-[#273142]/50">
              <button onClick={() => setWebSearch(!webSearch)} className={`flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase rounded-full border transition-all ${webSearch ? "bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/40" : "text-[#475569] border-[#273142]"}`}><Globe size={10}/> Search</button>
              <button onClick={() => setDeepReasoning(!deepReasoning)} className={`flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase rounded-full border transition-all ${deepReasoning ? "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/40" : "text-[#475569] border-[#273142]"}`}><Sparkles size={10}/> Deep</button>
            </div>
            <form onSubmit={handleSend} className="flex items-end gap-2 p-3">
              <Textarea data-testid="agent-chat-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder={`Ask ${agent.name}...`} disabled={isSending} rows={1} className="flex-1 bg-[#111620] border border-[#273142] text-white p-3 resize-none text-sm rounded-xl min-h-[48px] max-h-[200px] focus-visible:ring-1 focus-visible:ring-[#E5FF00] focus-visible:ring-offset-0"/>
              <button type="submit" data-testid="agent-chat-submit" disabled={isSending || !input.trim()} className="bg-[#E5FF00] text-[#080B10] w-11 h-11 rounded-xl hover:bg-[#D4EC00] transition-colors disabled:opacity-30 flex items-center justify-center flex-shrink-0">{isSending ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}</button>
            </form>
          </div>
        ) : (
          <div className="border-t border-[#273142] p-4 text-center"><SignInButton mode="modal"><button className="bg-[#E5FF00] text-[#080B10] font-semibold text-xs px-6 py-2.5 rounded-lg hover:bg-[#D4EC00]">Sign in to chat</button></SignInButton></div>
        )}
      </div>
      {related.length > 0 && (
        <div className="w-[260px] border-l border-[#273142] bg-[#080B10] hidden lg:flex flex-col">
          <div className="p-4 border-b border-[#273142]"><p className="font-mono text-[10px] uppercase tracking-widest text-[#475569] flex items-center gap-1"><Link2 size={10}/> Connected Agents</p></div>
          <ScrollArea className="flex-1"><div className="p-3 space-y-2">{related.map(r => <AgentCard key={r.id} agent={r} onClick={onSelectRelated} compact />)}</div></ScrollArea>
        </div>
      )}
    </div>
  );
}

export default function AgentPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: clerkUser } = useUser();
  const [home, setHome] = useState({ trending: [], recent: [], total_agents: 0, suggestions: [] });
  const [network, setNetwork] = useState({ nodes: [], edges: [] });
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [relatedAgents, setRelatedAgents] = useState([]);
  const [myNetwork, setMyNetwork] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Collaboration state
  const [promptInput, setPromptInput] = useState("");
  const [isCollaborating, setIsCollaborating] = useState(false);
  const [collabResult, setCollabResult] = useState(null);
  const [activeAgentIds, setActiveAgentIds] = useState([]);
  const [submittedPrompt, setSubmittedPrompt] = useState("");

  useEffect(() => {
    getAgentHome().then(res => setHome(res.data)).catch(() => {});
    getAgentNetwork().then(res => setNetwork(res.data)).catch(() => {});
  }, []);

  // Determine admin status from Clerk user email directly + backend check
  useEffect(() => {
    if (user) {
      getMyAgentNetwork().then(res => setMyNetwork(res.data)).catch(() => {});
      // Check admin via Clerk email (immediate) + backend (confirmation)
      const clerkEmail = (clerkUser?.primaryEmailAddress?.emailAddress || "").toLowerCase();
      if (clerkEmail === "rayan05rio@gmail.com") {
        setIsAdmin(true);
      }
      adminCheck().then(res => {
        if (res.data.is_admin) setIsAdmin(true);
      }).catch(() => {});
    } else {
      setIsAdmin(false);
    }
  }, [user, clerkUser]);

  const pollAgentPayment = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 5) { toast.info("Payment processing..."); return; }
    try {
      const res = await getPaymentStatus(sessionId);
      if (res.data.payment_status === "paid") {
        toast.success("Agent access purchased!");
        if (user) getMyAgentNetwork().then(res => setMyNetwork(res.data)).catch(() => {});
      } else {
        setTimeout(() => pollAgentPayment(sessionId, attempt + 1), 2000);
      }
    } catch { setTimeout(() => pollAgentPayment(sessionId, attempt + 1), 2000); }
  }, [user]);

  // Handle payment redirect for agent purchase
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const agentPurchased = searchParams.get("agent_purchased");
    if (sessionId && agentPurchased) {
      pollAgentPayment(sessionId);
      navigate("/agents", { replace: true });
    }
    const cancelled = searchParams.get("payment_cancelled");
    if (cancelled) {
      toast.info("Payment cancelled");
      navigate("/agents", { replace: true });
    }
  }, [searchParams, navigate, pollAgentPayment]);

  const handleSelectAgent = async (agent) => {
    if (!user) {
      toast.error("Sign in to chat with agents");
      return;
    }
    // Check if paid agent and user doesn't have access
    if (agent.pricing_mode === "paid" && user) {
      try {
        const accessRes = await checkAgentAccess(agent.id);
        if (!accessRes.data.has_access) {
          // Show buy access prompt
          setSelectedAgent({ ...agent, _needsAccess: true, _accessPrice: accessRes.data.access_price });
          setRelatedAgents([]);
          return;
        }
      } catch {}
    }
    try {
      const [agentRes, relatedRes] = await Promise.all([getAgent(agent.id), getRelatedAgents(agent.id)]);
      setSelectedAgent(agentRes.data);
      setRelatedAgents(relatedRes.data.related || []);
    } catch { setSelectedAgent(agent); setRelatedAgents([]); }
  };

  const handleBuyAccess = async (agent) => {
    if (!user) { toast.error("Sign in to purchase"); return; }
    try {
      const origin = window.location.origin;
      const res = await buyAgentAccess(agent.id, origin);
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    }
  };

  const handleCollabPrompt = async (e) => {
    e.preventDefault();
    if (!promptInput.trim() || isCollaborating) return;

    const prompt = promptInput.trim();
    setSubmittedPrompt(prompt);
    setIsCollaborating(true);
    setCollabResult(null);
    setActiveAgentIds([]);

    try {
      const res = await routerQuery(prompt);
      const data = res.data;
      const selectedIds = (data.routing?.selected_agents || []).map(a => a.id);
      setActiveAgentIds(selectedIds);
      setCollabResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Collaboration failed");
    } finally {
      setIsCollaborating(false);
    }
  };

  const handleReset = () => {
    setCollabResult(null);
    setActiveAgentIds([]);
    setSubmittedPrompt("");
    setPromptInput("");
  };

  const handleDeleteAgent = async (agent) => {
    try {
      const res = await adminDeleteAgent(agent.id);
      toast.success(`Agent "${agent.name}" deleted`);
      getAgentHome().then(res => setHome(res.data)).catch(() => {});
      getAgentNetwork().then(res => setNetwork(res.data)).catch(() => {});
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Delete failed";
      toast.error(`Delete failed: ${detail}`);
    }
  };

  // Paid agent — needs access gate
  if (selectedAgent?._needsAccess) {
    const agent = selectedAgent;
    const color = PROVIDER_COLORS[agent.provider_type] || "#94A3B8";
    return (
      <div data-testid="agent-access-gate" className="h-screen bg-[#080B10] flex flex-col">
        <div className="border-b border-[#273142] px-8 py-4 flex items-center gap-3">
          <button onClick={() => setSelectedAgent(null)} data-testid="agent-access-back" className="text-[#475569] hover:text-white transition-colors"><ArrowLeft size={18}/></button>
          <Lock size={16} className="text-[#E5FF00]" />
          <h2 className="text-sm font-semibold">{agent.name}</h2>
          <span className="font-mono text-[10px] text-[#E5FF00] px-2 py-0.5 rounded-full border border-[#E5FF00]/30 bg-[#E5FF00]/10">PAID AGENT</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 mx-auto rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: color + "20", border: `2px solid ${color}40` }}>
              <Lock size={28} className="text-[#E5FF00]" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{agent.name}</h3>
            <p className="text-sm text-[#94A3B8] mb-4">{agent.description || "This is a premium agent. Purchase access to start chatting."}</p>
            <div className="bg-[#111620] border border-[#273142] rounded-xl p-4 mb-4">
              <div className="flex items-center justify-center gap-2">
                <DollarSign size={18} className="text-[#E5FF00]" />
                <span className="text-2xl font-semibold text-[#E5FF00]">${(agent._accessPrice || 5).toFixed(2)}</span>
                <span className="font-mono text-[10px] text-[#475569] uppercase">one-time</span>
              </div>
            </div>
            <button data-testid="buy-agent-access-btn" onClick={() => handleBuyAccess(agent)}
              className="w-full bg-[#E5FF00] text-[#080B10] font-semibold text-sm px-6 py-3 rounded-xl hover:bg-[#D4EC00] transition-colors flex items-center justify-center gap-2">
              <Zap size={16} /> Buy Access
            </button>
            <button onClick={() => setSelectedAgent(null)} className="mt-3 text-xs text-[#475569] hover:text-white font-mono transition-colors">Back to network</button>
          </div>
        </div>
      </div>
    );
  }

  // Chat view (single agent)
  if (selectedAgent) {
    return (
      <div data-testid="agent-chat-page" className="h-screen bg-[#080B10] flex flex-col">
        <AgentChatView agent={selectedAgent} related={relatedAgents} user={user}
          onBack={() => setSelectedAgent(null)} onSelectRelated={(a) => handleSelectAgent(a)} />
      </div>
    );
  }

  return (
    <div data-testid="agent-page" className="h-screen bg-[#080B10] flex flex-col">
      {/* Floating Nav */}
      <FloatingNav onOpenChats={() => navigate("/chat")} onOpenMarketplace={() => navigate("/marketplace")} onOpenEarnings={() => navigate("/earnings")} onOpenProviders={() => navigate("/chat")} providerCount={0} />

      {/* Header */}
      <div className="border-b border-[#273142] px-8 py-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Anymind" className="w-6 h-6 rounded-md" />
          <h1 className="text-lg font-semibold tracking-tight">ANYMIND</h1>
        </div>
      </div>

      {/* Prompt Router Bar */}
      <div className="border-b border-[#273142] px-8 py-5 bg-gradient-to-b from-[#111620] to-[#080B10]">
        <form onSubmit={handleCollabPrompt} className="relative max-w-3xl mx-auto">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#E5FF00]" />
          <Input data-testid="agent-prompt-input" value={promptInput} onChange={e => setPromptInput(e.target.value)}
            placeholder="Search the Agent Internet — ask anything..."
            disabled={isCollaborating}
            className="bg-[#080B10] border-[#273142] text-white rounded-xl pl-11 pr-14 h-14 text-base focus:border-[#E5FF00] focus:ring-1 focus:ring-[#E5FF00] placeholder:text-[#475569]" />
          <button type="submit" data-testid="agent-prompt-submit" disabled={isCollaborating || !promptInput.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#E5FF00] text-[#080B10] w-10 h-10 rounded-lg hover:bg-[#D4EC00] transition-colors disabled:opacity-30 flex items-center justify-center">
            {isCollaborating ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </form>
        {!collabResult && !isCollaborating && (
          <div className="flex items-center gap-2 mt-3 max-w-3xl mx-auto flex-wrap justify-center">
            <span className="text-[10px] text-[#475569] font-mono">TRY:</span>
            {["Explain quantum computing simply", "Design a REST API for a todo app", "Compare React vs Vue in 2025", "Write a Python web scraper", "I'm feeling Anymind"].map(s => (
              <button key={s} onClick={() => { if (s === "I'm feeling Anymind") { setPromptInput("Surprise me with something fascinating — pick your best topic and blow my mind"); } else { setPromptInput(s); } }}
                className={`text-[10px] font-mono px-2.5 py-1 rounded-full transition-colors border ${s === "I'm feeling Anymind" ? "text-[#E5FF00] border-[#E5FF00]/40 hover:bg-[#E5FF00]/10" : "text-[#94A3B8] hover:text-[#E5FF00] border-[#273142] hover:border-[#E5FF00]/40"}`}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="px-8 py-6 max-w-6xl mx-auto">

          {/* Network Graph — ALWAYS visible */}
          {network.nodes.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Link2 size={14} className="text-[#E5FF00]"/>
                <h2 className="text-sm font-semibold">Agent Network</h2>
                <span className="font-mono text-[10px] text-[#475569]">{network.nodes.length} nodes, {network.edges.length} edges</span>
                {activeAgentIds.length > 0 && (
                  <span data-testid="active-agents-badge" className="ml-2 font-mono text-[10px] px-2 py-0.5 rounded-full bg-[#E5FF00]/10 border border-[#E5FF00]/30 text-[#E5FF00]">
                    {activeAgentIds.length} agent{activeAgentIds.length > 1 ? "s" : ""} active
                  </span>
                )}
                {isCollaborating && (
                  <span className="ml-2 flex items-center gap-1 font-mono text-[10px] text-[#E5FF00]">
                    <Loader2 size={10} className="animate-spin" /> Processing...
                  </span>
                )}
              </div>
              <div className="bg-[#111620] border border-[#273142] rounded-xl overflow-hidden transition-all duration-300"
                style={{ height: 380, borderColor: activeAgentIds.length > 0 ? "#E5FF0030" : undefined }}>
                <NetworkGraph
                  nodes={network.nodes}
                  edges={network.edges}
                  onSelectAgent={handleSelectAgent}
                  selectedId={null}
                  activeAgents={activeAgentIds}
                />
              </div>
              {!collabResult && !isCollaborating && (
                <p className="text-[10px] text-[#475569] font-mono mt-2 text-center">Click a node to open agent — hover for details</p>
              )}
            </div>
          )}

          {/* Inline Synthesized Response — appears below graph */}
          {(isCollaborating || collabResult) && (
            <div data-testid="collab-inline-result" className="mb-8 animate-slide-up">
              {/* User prompt echo */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-7 h-7 rounded-lg bg-[#1C2433] border border-[#273142] flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-mono text-[#94A3B8]">U</span>
                </div>
                <p className="text-sm pt-1 text-[#94A3B8]">{submittedPrompt || promptInput}</p>
              </div>

              {/* Loading state */}
              {isCollaborating && !collabResult && (
                <div data-testid="collab-loading" className="flex items-center gap-3 py-4 pl-10">
                  <Loader2 size={16} className="animate-spin text-[#E5FF00]" />
                  <span className="font-mono text-xs text-[#E5FF00]">Agents collaborating on your query...</span>
                </div>
              )}

              {/* Result */}
              {collabResult?.response && (
                <div className="bg-[#111620] border border-[#273142] border-l-2 border-l-[#E5FF00] rounded-xl p-5 animate-fade-in">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap size={12} className="text-[#E5FF00]" />
                    <span className="font-mono text-[10px] text-[#E5FF00] uppercase tracking-wider">Synthesized Answer</span>
                    {collabResult.routing?.response_time_ms && (
                      <span className="font-mono text-[10px] text-[#475569] ml-auto">{collabResult.routing.response_time_ms}ms</span>
                    )}
                  </div>
                  <div className="text-sm leading-relaxed">
                    <MarkdownRenderer content={collabResult.response} />
                  </div>

                  {/* Contributor chips */}
                  {collabResult.contributors?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-[#273142] flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] text-[#475569] uppercase">Powered by:</span>
                      {collabResult.contributors.map(c => (
                        <span key={c.agent_id} data-testid={`chip-${c.agent_id}`}
                          className="font-mono text-[10px] px-2 py-1 rounded-lg bg-[#E5FF00]/10 border border-[#E5FF00]/30 text-[#E5FF00] flex items-center gap-1">
                          <CheckCircle2 size={10} /> {c.agent_name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* No agents found */}
              {collabResult && !collabResult.contributors?.length && collabResult.response && (
                <div className="text-xs text-[#475569] font-mono mt-2 pl-10">No specific agents were selected for this query.</div>
              )}

              {/* Reset button */}
              {collabResult && !isCollaborating && (
                <button data-testid="collab-reset-btn" onClick={handleReset}
                  className="mt-3 text-xs text-[#475569] hover:text-[#E5FF00] font-mono transition-colors flex items-center gap-1">
                  <Zap size={10} /> Ask another question
                </button>
              )}
            </div>
          )}

          {/* Agents list (shown when no active collaboration) */}
          {!collabResult && !isCollaborating && (
            <>
              {home.recent.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4"><Clock size={14} className="text-[#3B82F6]"/><h2 className="text-sm font-semibold">Recently Created</h2></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {home.recent.map(a => <AgentCard key={a.id} agent={a} onClick={handleSelectAgent} isAdmin={isAdmin} onDelete={handleDeleteAgent} />)}
                  </div>
                </div>
              )}

              {home.recent.length === 0 && (
                <div className="text-center py-20"><Users size={32} className="text-[#273142] mx-auto mb-3"/><p className="text-[#475569] text-sm font-mono uppercase tracking-widest">No Agents Yet</p></div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
